import React, { useState, useEffect, useCallback } from "react";
import {
  MaterialReactTable,
  MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
} from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Container, Row, Accordion } from "react-bootstrap";
import { SelectOption } from "../../../Types/Customermarkupmetrix.types";
import { ReconBrokerOption, ReconContractMapping, ReconContractMappingFormData, ReconContractMappingFormErrors, ReconContractMappingFormTouched } from "../../../Types/ContractMap.type";
import { bulkDeleteReconContractMappings, createReconContractMapping, deleteReconContractMapping, EXPORT_URL, fetchReconContractMappings, fetchReconPhysicalCodes, importReconContractMappings, updateReconContractMapping } from "./contractmap.api";
import { CompactSelect, CompactTextField } from "../../../Components/SharedComponents/SharedComponents";
import { fetchBrokerOptions, fetchPhysicalCodeOptions } from "../../Trading/PaperTrading/FecthApis";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../../Components/ConfirmModal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { extractErrorMessage } from "../../../Tools/helpers/helpers";
import BrokerTemplateDownloadButton from "../../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileUpload from "../../../Components/FileUpload/FileUpload";
import { handleImportFileUpload } from "../Utils/handleFileUploadSubmit";
import { handleDataDownload } from "../Utils/handleDataDownload";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import { TableColumnVisibilityMenu } from "../../../Components/SharedComponents/TableColumnVisibilityMenu";
import { CommonToolbarInternalActions } from "../../../Components/SharedComponents/CommonTableToolbarActions";

// ─── Validation ───────────────────────────────────────────────────────────────

const validateField = (
  field: keyof ReconContractMappingFormData,
  value: string
): string => {
  if (!value) {
    const labels: Record<keyof ReconContractMappingFormData, string> = {
      broker: "Broker",
      contract: "Physical Code",
      external_contract_code: "External Contract Code",
    };
    return `${labels[field]} is required`;
  }
  return "";
};

const validateAll = (
  data: ReconContractMappingFormData
): ReconContractMappingFormErrors => {
  const errors: ReconContractMappingFormErrors = {};
  (Object.keys(data) as (keyof ReconContractMappingFormData)[]).forEach(
    (key) => {
      const msg = validateField(key, data[key]);
      if (msg) errors[key] = msg;
    }
  );
  return errors;
};


const INITIAL_FORM: ReconContractMappingFormData = {
  broker: "",
  contract: "",
  external_contract_code: "",
};


const ReconciliationContractMapping: React.FC = () => {
  const [formData, setFormData] =
    useState<ReconContractMappingFormData>(INITIAL_FORM);
  const [formErrors, setFormErrors] =
    useState<ReconContractMappingFormErrors>({});
  const [touched, setTouched] =
    useState<ReconContractMappingFormTouched>({});
  // const [editId, setEditId] = useState<string | null>(null);

  const [tableData, setTableData] = useState<ReconContractMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [brokerOptions, setBrokerOptions] = useState<SelectOption[]>([]);
  const [physicalCodeOptions, setPhysicalCodeOptions] = useState<SelectOption[]>([]);
  const [isBrokerLoading, setIsBrokerLoading] = useState(false);
  const [isPhysicalCodeLoading, setIsPhysicalCodeLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);
  

  const loadBrokerOptions = useCallback(async () => {
    setIsBrokerLoading(true);
    try {
      const { data, error } = await fetchBrokerOptions();
      if (error) {
        toast.error(`Error loading brokers: ${error}`);
        setBrokerOptions([]);
      } else {
        setBrokerOptions(
          Array.isArray(data)
            ? (data as ReconBrokerOption[]).map((item) => ({ label: item.name, value: item.id }))
            : []
        );
      }
    } catch {
      toast.error("Failed to load broker options");
      setBrokerOptions([]);
    } finally {
      setIsBrokerLoading(false);
    }
  }, []);

  const loadPhysicalCodeOptions = useCallback(async () => {
    setIsPhysicalCodeLoading(true);
    try {
      const data = await fetchReconPhysicalCodes();
      setPhysicalCodeOptions(
        data.map((item) => ({ label: item.physical_code, value: item.id }))
      );
    } catch {
      toast.error("Failed to load physical code options");
      setPhysicalCodeOptions([]);
    } finally {
      setIsPhysicalCodeLoading(false);
    }
  }, []);


  const loadData = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const data = await fetchReconContractMappings();
      setTableData(data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadBrokerOptions();
    loadPhysicalCodeOptions();
  }, [loadBrokerOptions, loadPhysicalCodeOptions]);


  // ── Form field helpers ────────────────────────────────────────────────────────

  const updateFormData = (
    field: keyof ReconContractMappingFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: validateField(field, value) || undefined }));
    }
  };

  const handleFocus = (field: keyof ReconContractMappingFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // ── Create / Update ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const allTouched: ReconContractMappingFormTouched = {
      broker: true,
      contract: true,
      external_contract_code: true,
    };
    setTouched(allTouched);
    const errors = validateAll(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setIsSaving(true);
    try {
      const created = await createReconContractMapping(formData);
      setTableData((prev) => [created, ...prev]);
      toast.success("Contract mapping created successfully!");
      setFormData(INITIAL_FORM);
      setTouched({});
      setFormErrors({});
    } catch (error) {
      toast.error(extractErrorMessage(error));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };


  const handleClear = () => {
    setFormData(INITIAL_FORM);
    setFormErrors({});
    setTouched({});
  };


  const handleDeleteRow = (row: MRT_Row<ReconContractMapping>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this entry?",
      content: `Deleting ${row.original.broker_name} - ${row.original.physical_code}`,
      onOk: () => {
        setIsSaving(true);
        deleteReconContractMapping(row.original.id)
          .then(() => {
            setTableData((prev) => prev.filter((item) => item.id !== row.original.id));
            toast.success("Record deleted successfully.");
          })
          .catch(() => toast.error("Delete failed."))
          .finally(() => setIsSaving(false));
      },
    });
  };

  const handleFileUploadSubmit = async (file: File) => {
    return await handleImportFileUpload(file, importReconContractMappings, loadData);
  };

  const handleContractDownload = () =>
    handleDataDownload(EXPORT_URL, "all_contracts_mapping.csv");
  const handleCopyRow = (row: MRT_Row<ReconContractMapping>) => {
    setFormData({
      broker: row.original.broker,
      contract: row.original.contract,
      external_contract_code: row.original.external_contract_code,
    });
    toast.success("Copied successfully!");  
  };

  const handleBulkDelete = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;

    showDeleteConfirmModal({
      title: "Are you sure you want to delete selected records?",
      content: `Deleting ${selectedIds.length} record(s)...`,
      onOk: () => {
        setIsSaving(true);
        bulkDeleteReconContractMappings(selectedIds)
          .then(() => {
            setTableData((prev) => prev.filter((item) => !selectedIds.includes(item.id)));
            setRowSelection({});
            toast.success(`${selectedIds.length} record(s) deleted successfully.`);
          })
          .catch(() => toast.error("Bulk delete failed. Please try again."))
          .finally(() => setIsSaving(false));
      },
    });
  };

  const handleEditingRowSave: MRT_TableOptions<ReconContractMapping>["onEditingRowSave"] =
    async ({ row, values, table }) => {
      const errors = validateAll({
        broker: values.broker,
        contract: values.contract,
        external_contract_code: values.external_contract_code,
      });
      if (Object.keys(errors).length > 0) {
        toast.error("Please fill in all required fields.");
        return;
      }
      setIsSaving(true);
      try {
        const updated = await updateReconContractMapping({
          id: row.original.id,
          broker: values.broker,
          contract: values.contract,
          external_contract_code: values.external_contract_code,
        });
        setTableData((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        toast.success("Contract mapping updated successfully!");
        table.setEditingRow(null);
      } catch (error) {
        toast.error(extractErrorMessage(error));
      } finally {
        setIsSaving(false);
      }
    };

  const columns: MRT_ColumnDef<ReconContractMapping>[] = [
    {
      accessorKey: "broker_name",
      header: "Broker Name",
      Cell: ({ row }) => row.original.broker_name,
      editVariant: "select",
      editSelectOptions: brokerOptions,
      size: 390,
      minSize: 150,
      muiEditTextFieldProps: {
        sx: {
          "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            height: "32px",
          },
          "& .MuiSelect-select": {
            fontSize: "0.7rem",
            padding: "4px 8px",
          },
        },
        SelectProps: {
          MenuProps: {
            PaperProps: {
              sx: {
                "& .MuiMenuItem-root": {
                  fontSize: "0.7rem",
                  minHeight: "28px",
                  padding: "4px 12px",
                },
              },
            },
          },
        },
      },
    },
    {
      accessorKey: "physical_code",
      header: "Physical Code",
      Cell: ({ row }) => row.original.physical_code,
      editVariant: "select",
      editSelectOptions: physicalCodeOptions,
      size: 240,
      minSize: 150,
      muiEditTextFieldProps: {
        sx: {
          "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            height: "32px",
          },
          "& .MuiSelect-select": {
            fontSize: "0.7rem",
            padding: "4px 8px",
          },
        },
        SelectProps: {
          MenuProps: {
            PaperProps: {
              sx: {
                "& .MuiMenuItem-root": {
                  fontSize: "0.7rem",
                  minHeight: "28px",
                  padding: "4px 12px",
                },
              },
            },
          },
        },
      },
    },
    {
      accessorKey: "external_contract_code",
      header: "External Contract Code",
      size: 390,
      editVariant: "text",
      muiEditTextFieldProps: {
        sx: {
          "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            height: "32px",
          },
          "& .MuiInputBase-input": {
            fontSize: "0.7rem",
            padding: "4px 8px",
          },
        },
      },
    },
  ];


  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableColumnPinning: true,
    enableDensityToggle: false,
    enableEditing: true,
    editDisplayMode: "row",
    enableColumnResizing: true,
    enableClickToCopy: true,
    onEditingRowSave: handleEditingRowSave,
    onEditingRowCancel: ({ table }) => table.setEditingRow(null),
    enableColumnOrdering: true,
    enableRowOrdering: false,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    initialState: { showColumnFilters: true },
    filterFns: {
      customFilterFn: (row, id, filterValue) =>
        row.getValue(id) === filterValue,
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
      },
    },
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),
    muiEditTextFieldProps: {
      sx: {
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: "32px",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.7rem",
        },
        "& .MuiSelect-select": {
          fontSize: "0.7rem",
          padding: "4px 8px",
        },
        "& .MuiMenuItem-root": {
          fontSize: "0.7rem",
        },
        minWidth: "120px",
      },
    },
      displayColumnDefOptions: {
            "mrt-row-actions": {
                size: 115,
                minSize: 115,
            },
            "mrt-row-select": {
                size: 40,
                minSize: 40,
            },
        },
    muiTableBodyCellProps: {
      sx: { border: "1px solid #ebe9e98f" },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "1",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
    } as any,
    muiToolbarAlertBannerProps: isError
      ? { color: "error", children: "Error loading data" }
      : undefined,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),

    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => handleDeleteRow(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
          Upload File
        </Button>
        <Button
          onClick={() => handleContractDownload()}
          startIcon={<BrowserUpdatedIcon />}
        >
          Export All Data
        </Button>
        {Object.keys(rowSelection).length > 0 && (
          <Button
            variant="text"
            color="error"
            disabled={isSaving}
            onClick={handleBulkDelete}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              height: "32px",
              color: "#d32f2f",
              fontWeight: 600,
            }}
          >
            Delete Selected ({Object.keys(rowSelection).length})
          </Button>
        )}
        <BrokerTemplateDownloadButton
          apiEndpoint="/paper-app/download-contract-mapping-template/"
          buttonText="Download Template"
          fileName="contractMapping_template.csv"
        />
      </Box>
    ),
    state: {
      isLoading,
      isSaving,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      showColumnFilters: true,
      rowSelection,
    },
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Reconciliation Contract Mapping
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div className="flex formWrapper">
                        <Box
                          sx={{
                            width: "100%",
                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            component="form"
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              gap: 0.5,
                              "& > *": {
                                minWidth: {
                                  xs: "100%",
                                  sm: "48%",
                                  md: "18%",
                                  lg: "12%",
                                  xl: "7.5%",
                                },
                              },
                            }}
                          >
                            {/* ── Broker dropdown ── */}
                            <CompactSelect
                              label="Broker"
                              value={formData.broker}
                              onChange={(value) =>
                                updateFormData("broker", value)
                              }
                              options={brokerOptions}
                              disabled={isBrokerLoading}
                              name="broker"
                              error={Boolean(
                                formErrors.broker && touched.broker
                              )}
                              validateOnBlur={(value) =>
                                validateField("broker", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() => handleFocus("broker")}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": {
                                  fontSize: "0.7rem",
                                  height: { xs: "36px", sm: "32px" },
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": {
                                    transform:
                                      "translate(14px, -6px) scale(0.75)",
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C0C0C0",
                                },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />

                            {/* ── Physical Code dropdown ── */}
                            <CompactSelect
                              label="Physical Code"
                              value={formData.contract}
                              onChange={(value) =>
                                updateFormData("contract", value)
                              }
                              options={physicalCodeOptions}
                              disabled={isPhysicalCodeLoading}
                              name="physical_code"
                              error={Boolean(
                                formErrors.contract && touched.contract
                              )}
                              validateOnBlur={(value) =>
                                validateField("contract", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() => handleFocus("contract")}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": {
                                  fontSize: "0.7rem",
                                  height: { xs: "36px", sm: "32px" },
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": {
                                    transform:
                                      "translate(14px, -6px) scale(0.75)",
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C0C0C0",
                                },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />

                            {/* ── External Contract Code ── */}
                            <CompactTextField
                              label="External Contract Code"
                              value={formData.external_contract_code}
                              onChange={(value) =>
                                updateFormData("external_contract_code", value)
                              }
                              name="external_contract_code"
                              error={Boolean(
                                formErrors.external_contract_code &&
                                touched.external_contract_code
                              )}
                              validateOnBlur={(value) =>
                                validateField("external_contract_code", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() =>
                                handleFocus("external_contract_code")
                              }
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": {
                                  fontSize: "0.7rem",
                                  height: { xs: "36px", sm: "32px" },
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": {
                                    transform:
                                      "translate(14px, -6px) scale(0.75)",
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C0C0C0",
                                },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />

                            <Button
                              onClick={handleSubmit}
                              variant="contained"
                              fullWidth
                              disabled={isSaving}
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "100px" },
                                maxWidth: { sm: "150px" },
                                backgroundColor: "#00bcd4",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": { backgroundColor: "#008c9e" },
                              }}
                            >
                              Add
                            </Button>

                            {/* ── Clear button ── */}
                            <Button
                              onClick={handleClear}
                              variant="outlined"
                              fullWidth
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "80px" },
                                maxWidth: { sm: "120px" },
                                textTransform: "none",
                                fontSize: "0.75rem",
                              }}
                            >
                              Clear
                            </Button>

                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="table-ad__wrapper">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                </div>
              </div>
            </div>
            <FileUpload
              open={openFileUpload}
              onFileUploadClose={handleFileUploadClose}
              onFileUploadSubmit={handleFileUploadSubmit}
              acceptedFileTypes=".csv"
            />
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default ReconciliationContractMapping;